/**
 * analysisRangeText.js : textes de la plage d'analyse, partagés par l'écran de
 * comparaison et son PDF (mêmes phrases des deux côtés). `t` = fonction de
 * traduction (useTranslation à l'écran, i18n.t dans le PDF) ; dates et nombres
 * selon la langue (formatDateLocale, formatNumberLocale).
 */
import { ALIGN_MODES } from '../charts/compareAxis';
import { formatHumanDuration } from '../utils/formatDuration';
import { formatDateLocale, formatNumberLocale, formatSessionTitle } from '../utils/formatters';
import { COMPARE_METRICS } from './compareMetrics';
import { MIN_COVERAGE, MIN_MEASURES_FOR_BEST } from './analysisRange';

const DATE_TIME = { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' };

/** Nom affiché d'une session comparée. */
export const sessionName = (s, i, t) =>
  formatSessionTitle(s.session?.title) || t('sessions.compare.fallbackName', { n: i + 1 });

/** Durée de la session entière (start_time → end_time), ou null. */
export function sessionFullDuration(session) {
  if (!session?.start_time || !session?.end_time) return null;
  return new Date(session.end_time) - new Date(session.start_time);
}

/** Ligne « Statistiques calculées du … au … (durée) » ou « de 0 à … depuis le début ». */
export function periodLine(resolved, t) {
  if (resolved.align === ALIGN_MODES.FROM_START) {
    return t('sessions.compare.range.statsFromStart', { duration: formatHumanDuration(resolved.elapsed) });
  }
  if (!resolved.clock) return '';
  const { start, end } = resolved.clock;
  return t('sessions.compare.range.statsClock', {
    from: formatDateLocale(start, DATE_TIME),
    to: formatDateLocale(end, DATE_TIME),
    duration: formatHumanDuration(end - start),
  });
}

/**
 * Durée de la session entière, suivie de la durée retenue dans la plage quand
 * la plage écarte au moins une minute de ses mesures (comparaison à l'étendue
 * des mesures, pas aux métadonnées : « Tout » n'affiche jamais « dont »).
 */
export function durationWithRange(fullMs, quality, t) {
  if (fullMs == null) return t('common.notProvided');
  const total = formatHumanDuration(fullMs);
  if (!quality || quality.spanDuration - quality.duration < 60000) return total;
  return t('sessions.compare.range.inRange', { total, retained: formatHumanDuration(quality.duration) });
}

/** En-tête de colonne du tableau des statistiques : durée et mesures retenues. */
export const columnHeader = (quality, t) => t('sessions.compare.range.header', {
  duration: formatHumanDuration(quality.duration), count: formatNumberLocale(quality.count, 0),
});

/**
 * Avertissements de qualité dans la plage, dans l'ordre d'affichage :
 * mesures tronquées au plafond de sécurité (D235), couverture insuffisante.
 * @returns {string[]}
 */
export function qualityWarnings(analysis, loaded, t) {
  const lines = [];
  analysis.sessions.forEach((q, i) => {
    if (!q.truncated) return;
    lines.push(t('sessions.compare.range.warnings.truncated', {
      name: sessionName(loaded[i], i, t),
      limit: formatNumberLocale(q.limit, 0),
      total: formatNumberLocale(q.total, 0),
    }));
  });
  analysis.sessions.forEach((q, i) => {
    if (q.coverage >= MIN_COVERAGE) return;
    lines.push(t('sessions.compare.range.warnings.coverage', {
      name: sessionName(loaded[i], i, t), percent: formatNumberLocale(q.coverage * 100, 0),
    }));
  });
  return lines;
}

/** Mentions sous le tableau : coches masquées (moins de 30 mesures), périodes différentes. */
export function tableNotes(analysis, t) {
  const notes = [];
  if (analysis.hideBestFields.length) {
    const metrics = COMPARE_METRICS.filter(m => analysis.hideBestFields.includes(m.field)).map(m => t(m.labelKey));
    notes.push(t('sessions.compare.range.warnings.fewMeasures', { metrics: metrics.join(', '), min: MIN_MEASURES_FOR_BEST }));
  }
  if (!analysis.overlap && analysis.filtered.length > 1) notes.push(t('sessions.compare.range.differentPeriods'));
  return notes;
}
